import { useLingui } from "@lingui/react/macro";
import type { BillingOverview } from "@orbin/contracts";
import { Button, Input, Label } from "@orbin/ui-web";
import { useEffect, useState } from "react";
import { fetchAuthCapabilities } from "../../lib/auth-capabilities";
import { billingErrorText } from "../../lib/billing-errors";
import { formatCpf, isValidCpf, toE164Brazil } from "../../lib/panel-format";
import { rpc } from "../../lib/rpc";

type Method = "phone" | "cpf";

/**
 * Starts the 7-day trial: no card, one per person. The person proves who they are with a
 * WhatsApp code or a CPF, whichever the server accepts.
 */
export function TrialStart({
  onStarted,
  onSkip,
}: {
  onStarted: (overview: BillingOverview) => void;
  onSkip?: () => void;
}) {
  const { t } = useLingui();
  const [methods, setMethods] = useState<Method[] | null>(null);
  const [method, setMethod] = useState<Method>("phone");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAuthCapabilities()
      .then((capabilities) => {
        const next: Method[] = [];
        if (capabilities.signup?.phoneVerification) next.push("phone");
        if (capabilities.signup?.cpfVerification) next.push("cpf");
        setMethods(next);
        if (next[0]) setMethod(next[0]);
      })
      .catch(() => setMethods([]));
  }, []);

  async function run(action: () => Promise<void>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(billingErrorText(cause, t`Something went wrong. Try again.`));
    } finally {
      setPending(false);
    }
  }

  const e164 = toE164Brazil(phone);

  if (methods === null) return null;
  if (methods.length === 0) {
    return (
      <p className="text-body2 text-muted-foreground">{t`The free trial is not available right now.`}</p>
    );
  }

  return (
    <form
      data-testid="trial-start"
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (method === "cpf") {
          if (!isValidCpf(cpf)) return setError(t`Enter a valid CPF`);
          const taxId = cpf.replace(/\D/g, "");
          void run(async () => {
            // The server stores the CPF before the trial starts, so checkout never asks for it again.
            onStarted(await rpc.billing.startTrial({ taxId }));
          });
        } else if (!e164) {
          setError(t`Enter a valid phone number`);
        } else if (!sent) {
          void run(async () => {
            await rpc.phone.sendCode({ phone: e164 });
            setSent(true);
          });
        } else {
          void run(async () => {
            await rpc.phone.verify({ phone: e164, code: code.trim() });
            onStarted(await rpc.billing.startTrial({ phone: e164 }));
          });
        }
      }}
    >
      {method === "phone" ? (
        <>
          <Label className="flex flex-col items-start gap-1.5 text-body2 font-normal">
            {t`WhatsApp number`}
            <Input
              inputMode="tel"
              autoComplete="tel"
              placeholder="(11) 91234-5678"
              disabled={sent}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </Label>
          {sent ? (
            <Label className="flex flex-col items-start gap-1.5 text-body2 font-normal">
              {t`6-digit code`}
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              />
            </Label>
          ) : null}
        </>
      ) : (
        <Label className="flex flex-col items-start gap-1.5 text-body2 font-normal">
          CPF
          <Input
            inputMode="numeric"
            placeholder="000.000.000-00"
            value={cpf}
            onChange={(event) => setCpf(formatCpf(event.target.value))}
          />
        </Label>
      )}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button type="submit" disabled={pending || (sent && code.length !== 6 && method === "phone")}>
        {method === "phone" && !sent ? t`Send code` : t`Start 7-day trial`}
      </Button>
      <div className="flex justify-between">
        {methods.length > 1 ? (
          <Button
            type="button"
            variant="text"
            size="sm"
            onClick={() => {
              setMethod(method === "phone" ? "cpf" : "phone");
              setError(null);
            }}
          >
            {method === "phone" ? t`Use CPF instead` : t`Use WhatsApp instead`}
          </Button>
        ) : (
          <span />
        )}
        {onSkip ? (
          <Button type="button" variant="text" size="sm" onClick={onSkip}>
            {t`Not now`}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
